import { Injectable, signal } from '@angular/core';

/**
 * Provides a single-slot optimistic undo for delete operations.
 *
 * Usage:
 *   // 1. Remove item from UI immediately (optimistic)
 *   this.items.update(list => list.filter(i => i.id !== id));
 *
 *   // 2. Register the deferred delete + undo callback
 *   this.undo.deleteWithUndo(
 *     'Item name',
 *     () => this.api.delete(id).subscribe(),  // fired after delay if not undone
 *     () => this.items.update(list => [item, ...list]) // fired if user clicks Undo
 *   );
 */
@Injectable({ providedIn: 'root' })
export class UndoService {
  /** Non-null while a delete is pending — holds the label shown in the toast. */
  readonly pending = signal<string | null>(null);

  private timer?: ReturnType<typeof setTimeout>;
  private _onDelete?: () => void;
  private _onUndo?: () => void;

  /**
   * Register a deferred delete operation.
   * Any previously pending delete is committed immediately before the new one starts.
   */
  deleteWithUndo(label: string, onDelete: () => void, onUndo: () => void, delay = 15000): void {
    this.commit();
    this._onDelete = onDelete;
    this._onUndo = onUndo;
    this.pending.set(label);
    this.timer = setTimeout(() => {
      this.pending.set(null);
      onDelete();
      this._onDelete = undefined;
      this._onUndo = undefined;
    }, delay);
  }

  /** Restore the item and cancel the pending API call. */
  undo(): void {
    if (!this.pending()) return;
    clearTimeout(this.timer);
    const fn = this._onUndo;
    this.pending.set(null);
    this._onDelete = undefined;
    this._onUndo = undefined;
    fn?.();
  }

  /** Immediately commit any pending delete (e.g. on page navigation). */
  commit(): void {
    if (!this.pending()) return;
    clearTimeout(this.timer);
    const fn = this._onDelete;
    this.pending.set(null);
    this._onDelete = undefined;
    this._onUndo = undefined;
    fn?.();
  }
}
